// Phase 5C — Inline quick-edit controls for a parts line row.
// Commits via the existing useUpdatePartsLine mutation — NO new logic.
//
// Editable: quantity, unit_cost, job_id. All other fields require the full
// edit modal. This intentionally avoids any field that could change pricing
// authority (unit_sell, manual_price_locked) or scheduling.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useUpdatePartsLine } from '@/hooks/parts/usePartsLines';
import type { PartsLine } from '@/hooks/parts/usePartsLines';
import type { JobOption } from '@/components/tickets/parts/PartsLineFormDialog';

interface Props {
  line: PartsLine;
  jobOptions: JobOption[];
  onClose: () => void;
}

export function PartsLineRowQuickEdit({ line, jobOptions, onClose }: Props) {
  const [qty, setQty] = useState(String(line.quantity ?? 1));
  const [cost, setCost] = useState(line.unit_cost == null ? '' : String(line.unit_cost));
  const [jobId, setJobId] = useState(line.job_id ?? '__ticket__');
  const updateMut = useUpdatePartsLine();

  const qtyNum = Number(qty);
  const costNum = cost === '' ? null : Number(cost);
  const qtyInvalid = !Number.isFinite(qtyNum) || qtyNum <= 0;
  const costInvalid = costNum != null && (!Number.isFinite(costNum) || costNum < 0);
  const blocked = qtyInvalid || costInvalid;

  const handleSave = async () => {
    if (blocked) return;
    try {
      await updateMut.mutateAsync({
        id: line.id,
        shopId: line.shop_id,
        ticketId: line.ticket_id,
        patch: {
          quantity: qtyNum,
          unit_cost: costNum,
          job_id: jobId === '__ticket__' ? null : jobId,
        } as any,
      });
      toast({ title: 'Part updated' });
      onClose();
    } catch (e: any) {
      toast({ title: 'Update failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="col-span-12 mt-2 rounded-md border bg-muted/20 p-2 space-y-2">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-2 space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Qty</label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-3 space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Cost ea</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-5 space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Attach to</label>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ticket__">Whole ticket</SelectItem>
              {jobOptions.map((j) => (
                <SelectItem key={j.job_id} value={j.job_id}>{j.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2"
            onClick={onClose}
            disabled={updateMut.isPending}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            className="h-8 px-2"
            onClick={handleSave}
            disabled={blocked || updateMut.isPending}
          >
            {updateMut.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Check className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      {(qtyInvalid || costInvalid) && (
        <div className="text-[10px] text-destructive">
          {qtyInvalid && 'Qty must be greater than 0. '}
          {costInvalid && 'Invalid cost.'}
        </div>
      )}
    </div>
  );
}
